// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IPolicyListItem } from 'src/types/d3ePolicy';
import { UserService } from 'src/services/user-service';
import { NotificationService } from 'src/services/notification-service';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';
import { IPartner, Partners } from '../../../types/partners';
import { D3eService } from '../../../services/d3e-service';
import { IUser } from '../../../types/user';
import { finalize } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { selectableTypes } from '../../../types/subscription';
import { WindowService } from '../../../services/window.service';

import {
  ISaveKeyDialogConfig,
  SaveKeyDialogComponent
} from '../../pages/manage-users/save-key-dialog/save-key-dialog.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ClipboardService } from '../../../services/clipboard.service';

dayjs.extend(utc);

export const PlanChoiceValue = {
  SELECT: 'd3ePersonal',
  PLUS: 'd3eBusiness',
  HARDWARE: 'hardware',
  BITLOCKER: 'BitLocker',
  HARDWARE2: 'hardware2',
  FDE: 'fde',
  AGENT: 'agent',
};

export type PlanChoices = 'd3ePersonal' | 'd3eBusiness' | 'hardware' | 'BitLocker' | 'hardware2' | 'fde' | 'agent';

export const MaxDrivePerInstallation = {
  HARDWARE: 5,
  HARDWARE2: 20
}

export interface IAddOrganizationData {
  policies: IPolicyListItem[];
  minDate: Date;
  maxDate: Date;
  expirationDate: Date;
  defaultDate: Date;
  maxDurationDays: number | null;
  choosePlan: boolean;
  edition: PlanChoices;
  availableLicenses: number;
  plusCount: number;
  selectCount: number;
  hardwareCount: number;
  hardware2Count: number;
  bitLockerCount: number;
  fdeCount: number;
  agentCount: number;
  maxCount: number;
  enableEndpointMap: boolean;
  enablePBAExtendedFeatures: boolean;
  enablePBALogErase: boolean;
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-add-organization',
    templateUrl: './add-organization.component.html',
    styleUrls: ['./add-organization.component.scss'],
    standalone: false
})
export class AddOrganizationComponent implements OnInit {
  registerForm: UntypedFormGroup;
  minDate: Date;
  maxDate: Date;
  expirationDate: Date;
  maxDurationDays: number;
  policies: IPolicyListItem[] = [];
  partners: IPartner[] = [];
  user: IUser = null;
  selected = '-1';
  checkingEmailExists = false;
  choosePlan = true;
  selectedLanguage: string;
  invalidExpiration = false;
  invalidDelay = false;
  plusCount: number;
  selectCount: number;
  hardwareCount: number;
  hardware2Count: number;
  fdeCount: number;
  agentCount: number;
  bitLockerCount: number;
  selectedPlanCount: number;
  selectedEdition: PlanChoices;
  maxDrivePerInstallation = MaxDrivePerInstallation.HARDWARE;
  previousDelay = 0;
  protected readonly PlanChoiceValue = PlanChoiceValue;

  @ViewChild('passwordInput') passwordInput: ElementRef;
  @ViewChild('durationDelay') durationDelay: ElementRef

  fTitle: string;
  fCompany: string;
  fEmail: string;
  fEMailInvalid: string;
  fExists: string;
  fPassword: string;
  fCopy: string;
  fFName: string;
  fLName: string;
  fLicenses: string;
  fAvailable: string;
  fDelay: string;
  fDuration: string;
  fExpires: string;
  fDefault: string;
  fSystemDefault: string;
  fNotify: string;
  fCancel: string;
  fSave: string;
  fPersonal: string;
  fBusiness: string;
  subTypes = selectableTypes;
  subType = selectableTypes[selectableTypes.length - 1];

  constructor(
    public dialogRef: MatDialogRef<AddOrganizationComponent>,
    public dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private notificationService: NotificationService,
    private userService: UserService,
    private d3eService: D3eService,
    private translate: TranslateService,
    private windowService: WindowService,
    @Inject(MAT_DIALOG_DATA) public data: IAddOrganizationData,
    private readonly clipboard: ClipboardService,
  ) {
    this.translate.get(['CANCEL_CAP', 'SAVE_CAP', 'ADO_TITLE', 'ADO_COMPANY', 'ADO_EMAIL', 'ADO_EXISTS', 'APU_EMAIL_INVALID',
      'ADO_PASS', 'ADO_COPY', 'ADO_LICENSES', 'ADO_AVAILABLE', 'ADO_EXPIRES', 'ADO_DELAY', 'ADO_DURATION', 'ADO_DEFAULT',
      'ADO_SYS_DEFAULT', 'ADO_NOTIFY', 'FIRST', 'LAST', 'ADS_PERSONAL', 'ADS_PREMIUM']).subscribe(vals => {
      this.fCancel = vals['CANCEL_CAP'];
      this.fSave = vals['SAVE_CAP'];
      this.fTitle = vals['ADO_TITLE'];
      this.fCompany = vals['ADO_COMPANY'];
      this.fEmail = vals['ADO_EMAIL'];
      this.fExists = vals['ADO_EXISTS'];
      this.fEMailInvalid = vals['APU_EMAIL_INVALID'];
      this.fPassword = vals['ADO_PASS'];
      this.fCopy = vals['ADO_COPY'];
      this.fLicenses = vals['ADO_LICENSES'];
      this.fAvailable = vals['ADO_AVAILABLE'];
      this.fDelay = vals['ADO_DELAY'];
      this.fDuration = vals['ADO_DURATION'];
      this.fExpires = vals['ADO_EXPIRES'];
      this.fDefault = vals['ADO_DEFAULT'];
      this.fSystemDefault = vals['ADO_SYS_DEFAULT'];
      this.fNotify = vals['ADO_NOTIFY'];
      this.fFName = vals['FIRST'];
      this.fLName = vals['LAST'];
      this.fPersonal = vals['ADS_PERSONAL'];
      this.fBusiness = vals['ADS_PREMIUM'];
    });
    this.d3eService.getPartners()
      .pipe(untilDestroyed(this))
      .subscribe(result => {
        this.partners = result.partners;
    });
    this.userService.getUser()
      .pipe(untilDestroyed(this))
      .subscribe(user => {
        this.user = user;
    });
    this.selectedEdition = data.edition;
    this.registerForm = this.formBuilder.group({
      company: ['', [Validators.required, Validators.maxLength(255)]],
      email: ['', [Validators.required, Validators.pattern(CustomValidators.emailPattern),
        CustomValidators.maxByteLength(255)]],
      reserve_domain: [false],
      password: ['', Validators.compose([Validators.required, Validators.minLength(12)])],
      fname: ['', [Validators.required, Validators.maxLength(255)]],
      lname: ['', [Validators.required, Validators.maxLength(255)]],
      expirationDate: [dayjs.utc().add(1, 'year').add(1, 'day').endOf('day').toDate()],
      maintenance_end: [dayjs.utc().add(1, 'year').add(1, 'day').endOf('day').toDate(), Validators.required],
      licenses: ['', Validators.required],
      edition: [data.edition, Validators.required],
      enableEndpointMap: [true],
      defaultPolicy: ['-1'],
      partnerId: ['-1'],
      sendEmail: [true],
      generateKeys: [true],
      keyPassword: [''],
      enablePBAExtendedFeatures: [true],
      enablePBALogErase: [false],
    });
    this.registerForm.controls['password'].setValue(this.generatePassword());
    this.policies = data.policies;
    this.minDate = data.minDate;
    this.maxDate = data.maxDate;
    this.expirationDate = data.defaultDate;
    this.maxDurationDays = data.maxDurationDays;
    this.selectedLanguage = localStorage.getItem('language') || 'en';
    this.choosePlan = data.choosePlan;
    this.plusCount = data.plusCount;
    this.selectCount = data.selectCount;
    this.hardwareCount = data.hardwareCount;
    this.fdeCount = data.fdeCount;
    this.agentCount = data.agentCount;
    this.hardware2Count = data.hardware2Count;
    this.bitLockerCount = data.bitLockerCount;
    this.selectedPlanCount = this.getSelectedPlanCount(data);
    this.registerForm.controls['enablePBAExtendedFeatures'].setValue(data.enablePBAExtendedFeatures);
    this.registerForm.controls['enablePBALogErase'].setValue(data.enablePBALogErase);
    if (this.registerForm.controls.generateKeys.value !== true) {
      this.registerForm.controls.keyPassword.disable();
    }

  }

  private getSelectedPlanCount(data) {
    if ( data.edition === PlanChoiceValue.PLUS) {
      return data.plusCount;
    }
    if ( data.edition === PlanChoiceValue.HARDWARE ) {
      return data.hardwareCount;
    }
    if ( data.edition === PlanChoiceValue.HARDWARE2 ) {
      return data.hardware2Count;
    }
    if ( data.edition === PlanChoiceValue.BITLOCKER ) {
      return data.bitLockerCount;
    }
    if ( data.edition === PlanChoiceValue.FDE ) {
      return data.fdeCount;
    }
    if (data.edition === PlanChoiceValue.AGENT) {
      return data.agentCount;
    }
    // default to select count
    return data.selectCount;
  }

  ngOnInit(): void {
      this.subType = selectableTypes[selectableTypes.length - 1];
  }

  generatePassword() {
    const length = 20,
        charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789~!@#$%^&*';
    let retVal: string;
    do {
      retVal = '';
      for (let i = 0, n = charset.length; i < length; ++i) {
        retVal += charset.charAt(Math.floor(this.windowService.cryptoRandom() * n));
      }
    } while (!this.isValidPassword(retVal));
    return retVal;
  }

  isValidPassword(password: string): boolean {
    if (!/\d/.test(password)) {
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      return false;
    }
    if (!/[a-z]/.test(password)) {
      return false;
    }
    if (!/[^ \w]|_/.test(password)) {
      return false;
    }
    return true;
  }

  copyPasswordToClipboard() {
    this.clipboard.copyMessage(this.passwordInput.nativeElement.value, this.translate.instant('COPIED_TO_CLIPBOARD')).then(null);
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onSubmit() {
    this.maybeSave();
  }

  onExpirationChanged() {
    this.resetDurationValues();
  }

  resetDurationValues() {
    if (this.durationDelay && this.durationDelay.nativeElement) {
        this.durationDelay.nativeElement.value = null;
    }
    this.maxDurationDays = null;
    this.previousDelay = 0;
  }

  onDelayChange(ev: any) {
    const delayMonths = +ev.target.value;
    const delta = delayMonths - this.previousDelay;
    this.minDate = dayjs.utc(this.data.minDate).add(delayMonths, 'months').toDate();
    this.maxDate = dayjs.utc(this.data.maxDate).add(delayMonths, 'months').toDate();
    this.registerForm.controls['expirationDate'].setValue(
      dayjs.utc(this.registerForm.value['expirationDate']).add(delta, 'months').toDate());
    this.registerForm.controls['maintenance_end'].setValue(
      dayjs.utc(this.registerForm.value['maintenance_end']).add(delta, 'months').toDate());
    this.previousDelay = delayMonths;

    const today = dayjs();
    const futureDay = dayjs(this.registerForm.controls.expirationDate.value);
    this.maxDurationDays = futureDay.diff(today, 'day');
  }

  maybeSave() {
    let enableEndPointMap: boolean;
    if (this.selectedEdition === PlanChoiceValue.HARDWARE ||
        this.selectedEdition === PlanChoiceValue.HARDWARE2 ||
        this.selectedEdition === PlanChoiceValue.FDE ||
        this.selectedEdition === PlanChoiceValue.AGENT
    ) {
      enableEndPointMap = false;
      this.maxDurationDays = null;
    } else {
      enableEndPointMap = this.registerForm.controls.enableEndpointMap.value;
    }

    const values = {
      ...this.registerForm.value,
      maxDurationDays: this.maxDurationDays,
      email_notify_language: this.selectedLanguage,
      subType: this.subType.value,
      generateKeys: this.registerForm.controls.generateKeys.value,
      keyPassword: this.registerForm.controls.keyPassword.value,
      enableEndpointMap: enableEndPointMap,
    };
    if (values['generateKeys'] && values['keyPassword']) {
      const dialogConfig: ISaveKeyDialogConfig = {password: values.keyPassword};
      const saveDialogRef = this.dialog.open(SaveKeyDialogComponent, {
        width: '600px',
        height: 'auto',
        disableClose: true,
        data: dialogConfig,
      });
      saveDialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.dialogRef.close(values);
        }
      });
    } else {
      this.dialogRef.close(values);
    }
  }

  get email() {
    return this.registerForm.controls['email'];
  }

  get doNotSendEmail() {
    return !this.registerForm.controls['sendEmail']?.value;
  }

  onEmailBlur() {
    if (this.isEmailFieldInvalid('email')) {
      // Email is invalid, don't check email yet!
    } else {
      if (this.registerForm.controls.email.errors) {
        return;
      }

      this.checkingEmailExists = true;
      this.userService.checkUserEmailExists(this.registerForm.value.email)
          .pipe(
              finalize(() => this.checkingEmailExists = false),
              untilDestroyed(this)
          )
          .subscribe(
              next => {
                // This is a case where we only consider if the email exists regardless of verified or invited.
                if (next.exists) {
                  this.email.setErrors({'exists': true});
                }
              },
              () => {
                // Error state, probably email pattern
                this.email.setErrors({'pattern': true});
              }
          );
    }
  }

  isEmailFieldInvalid(field: string) {
    return (
      (!this.registerForm.get(field).valid && this.registerForm.get(field).touched) ||
      (this.registerForm.get(field).untouched)
    );
  }

  onLanguageChanged(language) {
    this.selectedLanguage = language;
  }

  onEditionChanged(edition: PlanChoices) {
    const defaultExpiration = dayjs.utc().add(1, 'year').endOf('day').toDate();
    const defaultFdeExpiration = dayjs.utc('2050-01-01T00:00:00Z').endOf('day').toDate();
    const defaultFdeMaintenanceDate = dayjs.utc().add(3, 'year').endOf('day').toDate();

    if ( edition === PlanChoiceValue.SELECT ) {
      this.selectedPlanCount = this.selectCount;
      this.registerForm.controls['expirationDate'].setValue(defaultExpiration);
      this.registerForm.controls['maintenance_end'].setValue(defaultExpiration);
    } else if ( edition === PlanChoiceValue.HARDWARE ) {
      this.selectedPlanCount = this.hardwareCount;
      this.maxDrivePerInstallation = MaxDrivePerInstallation.HARDWARE;
      this.registerForm.controls['expirationDate'].setValue(null);
      this.registerForm.controls['maintenance_end'].setValue(defaultExpiration);
    } else if ( edition === PlanChoiceValue.HARDWARE2 ) {
      this.selectedPlanCount = this.hardware2Count;
      this.maxDrivePerInstallation = MaxDrivePerInstallation.HARDWARE2;
      this.registerForm.controls['expirationDate'].setValue(null);
      this.registerForm.controls['maintenance_end'].setValue(defaultExpiration);
    } else if ( edition === PlanChoiceValue.BITLOCKER ) {
      this.selectedPlanCount = this.bitLockerCount;
      this.registerForm.controls['expirationDate'].setValue(defaultExpiration);
      this.registerForm.controls['maintenance_end'].setValue(defaultExpiration);
    } else if ( edition === PlanChoiceValue.FDE ) {
      this.selectedPlanCount = this.fdeCount;
      this.registerForm.controls['expirationDate'].setValue(defaultFdeExpiration);
      this.registerForm.controls['maintenance_end'].setValue(defaultFdeMaintenanceDate);
    } else if ( edition === PlanChoiceValue.AGENT ) {
      this.selectedPlanCount = this.agentCount;
      this.registerForm.controls['expirationDate'].setValue(defaultExpiration);
      this.registerForm.controls['maintenance_end'].setValue(defaultExpiration);
    } else {
      this.selectedPlanCount = this.plusCount;
      this.registerForm.controls['expirationDate'].setValue(defaultExpiration);
      this.registerForm.controls['maintenance_end'].setValue(defaultExpiration);
    }
    this.selectedEdition = edition;
    this.resetDurationValues();
  }

  onPartnerChanged(partnerId: string) {
    const partnerDefaultPBAExtendedFeatures = partnerId !== Partners.Digistor.toString();
    const partnerDefaultPBALogErase = partnerId === Partners.Digistor.toString();
    this.registerForm.controls['enablePBAExtendedFeatures'].setValue(partnerDefaultPBAExtendedFeatures);
    this.registerForm.controls['enablePBALogErase'].setValue(partnerDefaultPBALogErase);
  }

  changePasswordEnabled(event) {
    const control = this.registerForm.get('keyPassword');
    if (event.checked) {
      control.enable();
    } else {
      control.disable();
    }
  }

}
